import {
	PLAN_REVIEW_FEEDBACK_REASON_OPTIONS,
	PLAN_REVIEW_FINDING_STATUS_OPTIONS,
	PLAN_REVIEW_SEVERITY_OPTIONS
} from "constants/constants";
import {
	PlanReviewFeedbackDecision,
	PlanReviewFindingChangeAction,
	isAuthorityRole,
	isBlockingPlanReviewFindingStatus
} from "constants/field-values";

/** One plan_review_finding_changes row, as get_plan_review_findings returns it (oldest first). */
export interface PlanReviewFindingChangeRow {
	id: string;
	user_role: string;
	user_name?: string | null;
	organization_name?: string | null;
	action: string;
	/** List of { field, before, after }. */
	changes: unknown;
	reason?: string | null;
	created_at: string;
}

/** One plan_review_feedback row, as get_plan_review_findings returns it (newest first). */
export interface PlanReviewFindingFeedbackRow {
	id: string;
	user_role: string;
	user_name?: string | null;
	organization_name?: string | null;
	decision?: string | null;
	reason?: string | null;
	comment?: string | null;
	created_at: string;
}

/** The fields of a finding that the review features read. The report rows have all of them. */
export interface ReviewableFinding {
	status?: string | null;
	severity?: string | null;
	summary?: string | null;
	corrective_action?: string | null;
	ollie_status?: string | null;
	ollie_severity?: string | null;
	ollie_summary?: string | null;
	ollie_corrective_action?: string | null;
	/** Code sections set by the reviewer. Null when the finding uses its requirement's sections. */
	code_sections?: unknown;
	created_at?: string | null;
	plan_review_finding_changes?: PlanReviewFindingChangeRow[];
	plan_review_feedbacks?: PlanReviewFindingFeedbackRow[];
}

/** One changed field, with the values already written as display text. */
export interface FindingHistoryFieldChange {
	label: string;
	before: string;
	after: string;
}

/** One entry of the change history shown under a finding, oldest first. */
export interface FindingHistoryEntry {
	key: string;
	at: string;
	name: string;
	organization: string | null;
	role: string;
	action: string;
	fieldChanges: FindingHistoryFieldChange[];
	/** Lines that are not a before/after pair, for example "Status: Fail". */
	notes: string[];
	reason: string | null;
}

const FIELD_LABELS: Record<string, string> = {
	status: "Status",
	severity: "Severity",
	summary: "Observation",
	corrective_action: "Recommended Action",
	code_sections: "Code Sections"
};

const DECISION_LABELS: Record<string, string> = {
	[PlanReviewFeedbackDecision.CONFIRMED]: "Confirmed",
	[PlanReviewFeedbackDecision.PARTIALLY_CORRECT]: "Partially Correct",
	[PlanReviewFeedbackDecision.INCORRECT]: "Incorrect"
};

const findOptionLabel = (options: readonly { value: string; label: string }[], value?: string | null): string | null =>
	value ? (options.find(option => option.value === value)?.label ?? value) : null;

export const findingStatusLabel = (status?: string | null): string | null =>
	findOptionLabel(PLAN_REVIEW_FINDING_STATUS_OPTIONS, status);

export const findingSeverityLabel = (severity?: string | null): string | null =>
	findOptionLabel(PLAN_REVIEW_SEVERITY_OPTIONS, severity);

export const feedbackReasonLabel = (reason?: string | null): string | null =>
	findOptionLabel(PLAN_REVIEW_FEEDBACK_REASON_OPTIONS, reason);

export const feedbackDecisionLabel = (decision?: string | null): string | null =>
	decision ? (DECISION_LABELS[decision] ?? decision) : null;

/** "Capt. Jordan Reyes (TCESD No. 2)", or only the name when the organization is unknown. */
export const formatPersonWithOrganization = (name?: string | null, organization?: string | null): string => {
	const personName = name || "Unknown user";
	return organization ? `${personName} (${organization})` : personName;
};

/** "Oct 29, 2026, 3:14 PM" in the reader's time zone. */
export const formatReviewDateTime = (isoDate: string): string =>
	new Date(isoDate).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

/** The four values of a finding that a reviewer can change, as text or null. */
export interface FindingReviewValues {
	status: string | null;
	severity: string | null;
	summary: string | null;
	correctiveAction: string | null;
}

const currentValuesOf = (finding: ReviewableFinding): FindingReviewValues => ({
	status: finding.status ?? null,
	severity: finding.severity ?? null,
	summary: finding.summary ?? null,
	correctiveAction: finding.corrective_action ?? null
});

/**
 * The values that Ollie wrote. A finding saved before the backend kept these copies has
 * ollie_status null: nobody changed it, so its current values are still Ollie's.
 */
export const ollieValuesOf = (finding: ReviewableFinding): FindingReviewValues =>
	finding.ollie_status
		? {
				status: finding.ollie_status,
				severity: finding.ollie_severity ?? null,
				summary: finding.ollie_summary ?? null,
				correctiveAction: finding.ollie_corrective_action ?? null
			}
		: currentValuesOf(finding);

/** The fields whose current value is not the one Ollie wrote. */
export const changedFindingFields = (finding: ReviewableFinding) => {
	const current = currentValuesOf(finding);
	const ollie = ollieValuesOf(finding);
	return {
		status: current.status !== ollie.status,
		severity: current.severity !== ollie.severity,
		summary: current.summary !== ollie.summary,
		correctiveAction: current.correctiveAction !== ollie.correctiveAction,
		codeSections: Array.isArray(finding.code_sections)
	};
};

/** True when any value of the finding is not the one Ollie wrote. */
export const isFindingEditedByReviewer = (finding: ReviewableFinding): boolean =>
	Object.values(changedFindingFields(finding)).some(Boolean);

/** True when Ollie's status of the finding needs a revision. */
export const isOllieStatusBlocking = (finding: ReviewableFinding): boolean =>
	isBlockingPlanReviewFindingStatus(ollieValuesOf(finding).status);

/** The newest reviewer change of the finding, or null when nobody changed it. */
export const latestReviewerChange = (finding: ReviewableFinding): PlanReviewFindingChangeRow | null => {
	const changes = finding.plan_review_finding_changes ?? [];
	return changes.length > 0 ? changes[changes.length - 1] : null;
};

/** True when the last reviewer action on the finding was a revert to Ollie's original. */
export const isFindingRevertedToOllie = (finding: ReviewableFinding): boolean =>
	latestReviewerChange(finding)?.action === PlanReviewFindingChangeAction.REVERTED &&
	!isFindingEditedByReviewer(finding);

/** The newest applicant feedback of the finding. Feedback rows arrive newest first. */
export const latestApplicantFeedback = (finding: ReviewableFinding): PlanReviewFindingFeedbackRow | null =>
	(finding.plan_review_feedbacks ?? []).find(feedback => !isAuthorityRole(feedback.user_role)) ?? null;

/** The newest reviewer feedback of the finding. Feedback rows arrive newest first. */
export const latestReviewerFeedback = (finding: ReviewableFinding): PlanReviewFindingFeedbackRow | null =>
	(finding.plan_review_feedbacks ?? []).find(feedback => isAuthorityRole(feedback.user_role)) ?? null;

/**
 * True when the applicant marked a finding that still needs a revision as Incorrect. These findings
 * move to the "Marked incorrect" group. They still block the approval until the reviewer changes them.
 */
export const isMarkedIncorrectByApplicant = (finding: ReviewableFinding): boolean =>
	isBlockingPlanReviewFindingStatus(finding.status) &&
	latestApplicantFeedback(finding)?.decision === PlanReviewFeedbackDecision.INCORRECT;

const formatFieldValue = (field: string, value: unknown): string => {
	if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : "None";
	if (value === null || value === undefined || value === "") return "None";
	if (field === "status") return findingStatusLabel(String(value)) ?? String(value);
	if (field === "severity") return findingSeverityLabel(String(value)) ?? String(value);
	return String(value);
};

const toFieldChanges = (changes: unknown): FindingHistoryFieldChange[] => {
	if (!Array.isArray(changes)) return [];
	return changes.map(change => ({
		label: FIELD_LABELS[change?.field] ?? String(change?.field ?? "Change"),
		before: formatFieldValue(change?.field, change?.before),
		after: formatFieldValue(change?.field, change?.after)
	}));
};

const buildOllieEntry = (finding: ReviewableFinding): FindingHistoryEntry => {
	const ollieValues = ollieValuesOf(finding);
	const notes = [`Status: ${findingStatusLabel(ollieValues.status) ?? "—"}`];
	const ollieSeverity = findingSeverityLabel(ollieValues.severity);
	if (ollieSeverity) notes.push(`Severity: ${ollieSeverity}`);
	return {
		key: "ollie",
		at: finding.created_at ?? "",
		name: "Ollie",
		organization: null,
		role: "AI Plan Review",
		action: "Initial compliance analysis",
		fieldChanges: [],
		notes,
		reason: null
	};
};

const buildChangeEntry = (change: PlanReviewFindingChangeRow): FindingHistoryEntry => {
	const isRevert = change.action === PlanReviewFindingChangeAction.REVERTED;
	return {
		key: `change-${change.id}`,
		at: change.created_at,
		name: change.user_name || "Reviewer",
		organization: change.organization_name ?? null,
		role: "Reviewer",
		action: isRevert ? "Reverted to Ollie’s original" : "Overrode finding",
		fieldChanges: toFieldChanges(change.changes),
		notes: [],
		reason: change.reason ?? null
	};
};

const buildFeedbackEntry = (feedback: PlanReviewFindingFeedbackRow): FindingHistoryEntry => {
	const role = isAuthorityRole(feedback.user_role) ? "Reviewer" : "Applicant";
	const decision = feedbackDecisionLabel(feedback.decision) ?? "Feedback";
	const reason = feedbackReasonLabel(feedback.reason);
	return {
		key: `feedback-${feedback.id}`,
		at: feedback.created_at,
		name: feedback.user_name || role,
		organization: feedback.organization_name ?? null,
		role,
		action: `${role} feedback: ${decision}`,
		fieldChanges: [],
		notes: [`Rating: ${decision}${reason ? ` · Reason: ${reason}` : ""}`],
		reason: feedback.comment || null
	};
};

/**
 * The change history of a finding, oldest first: Ollie's analysis, every reviewer change and every
 * applicant feedback. Reviewer feedback rates Ollie for the AHJ, so it is only added for reviewers.
 */
export const buildFindingHistory = (
	finding: ReviewableFinding,
	{ includeReviewerFeedback }: { includeReviewerFeedback: boolean }
): FindingHistoryEntry[] => {
	const changeEntries = (finding.plan_review_finding_changes ?? []).map(buildChangeEntry);
	const feedbackEntries = (finding.plan_review_feedbacks ?? [])
		.filter(feedback => includeReviewerFeedback || !isAuthorityRole(feedback.user_role))
		.map(buildFeedbackEntry);

	const peopleEntries = [...changeEntries, ...feedbackEntries].sort(
		(first, second) => new Date(first.at).getTime() - new Date(second.at).getTime()
	);
	return [buildOllieEntry(finding), ...peopleEntries];
};
